"use client";
import { submitNewsletterSubscription } from "@/app/actions";
import { Toaster } from "@/components/ui/sonner";
import Image from "next/image";
import { useState } from "react";
import { toast } from "sonner";

interface SubscriptionFormProps {
  onSubmit?: (data: { email: string }) => void;
  isLoading?: boolean;
}

export default function SubscriptionForm({ onSubmit, isLoading: externalLoading }: SubscriptionFormProps) {
  const [email, setEmail] = useState("");
  const [internalLoading, setInternalLoading] = useState(false);

  // Use external loading state if provided, otherwise use internal state
  const isLoading = externalLoading !== undefined ? externalLoading : internalLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (onSubmit) {
      // Use external onSubmit if provided
      onSubmit({ email });
      setEmail("");
    } else {
      // Use server action for newsletter subscription
      setInternalLoading(true);

      try {
        const result = await submitNewsletterSubscription({ email });

        if (result.success) {
          toast.success('Done, You are onboarded.');
          setEmail("");
        } else {
          toast.error(result.message);
        }
      } catch (error) {
        console.error("Newsletter subscription error:", error);
        toast.error("Something went wrong. Please try again.");
      } finally {
        setInternalLoading(false);
      }
    }
  };

  return (
    <div className="bg-primary from-indigo-50 to-blue-50">
      <Toaster />
      <section className="relative overflow-hidden max-w-6xl rounded-2xl mx-auto">
        {/* Background Image */}
        {/* <div className="absolute inset-0">
          <Image
            src="/images/case-studies/cta-bg.webp"
            alt="CTA Background"
            fill
            className="object-cover"
            priority
          />
        </div> */}

        {/* Content */}
        <div className="relative z-10 px-6 py-20 sm:px-8 sm:py-24 lg:px-12 lg:py-28">
          <div className="max-w-2xl mx-auto text-center">
            {/* Title */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-8 leading-tight">
              We aim to create tech<br />easy for everyone.
            </h2>

            {/* Description */}
            <p className="text-lg sm:text-xl text-white/60 mb-12 max-w-2xl mx-auto">
              Join the flight. We will never spam. That's our promise.
            </p>

            {/* Email Form */}
            <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <div className="flex-1">
                <input
                  type="email"
                  placeholder="Your Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="w-full px-4 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-3 bg-white/5 backdrop-blur-sm border border-white/30 text-white font-medium rounded-full hover:bg-white/30 hover:border-white/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Subscribing...
                  </div>
                ) : (
                  'Subscribe'
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
